'use strict';

import { CONST } from 'app_vodafone_ziggo/util/util';
import { sendGtmRouteChangeEvent } from '../util/gtm';
import initLetsTalk from './letsTalk';
import { setActiveProductIDs } from '../components/setActiveProductIDs';
import 'Components/form/VZSelect';
import 'Components/form/VZDatePicker';

/**
 * @constant {Object} selectors - All used selectors
 * @property {string} toggle - Toggle selector
 */
const selectors = {
    mainContainer: '.js-bscan-main',
    column: '.col',
    allAnswersField: '.js-bscan-answers',
    adviceStep: '.js-bscan-step.bscan-advice',
    chatButton: '.dimelo_chat_item_markup',
    ctcElement: '.js-copy-to-clipboard',
    productTile: '.js-bscan-product-tile',
    adviceJSON: '.js-advice-json',
    productIDs: '.js-product-ids',
    notRecommendedWrapper: '.js-not-recommended-products',
    formFields: {
        firstName: '.js-form-first-name',
        lastName: '.js-form-last-name',
        email: '.js-form-email',
        optIn: '.js-form-optin',
        phone: '.js-form-phone'
    },
    pricing: {
        colTotalPrice: '.js-col-total-price',
        totalPrice: '.js-total-price',
        totalPriceContainer: '.js-total-price-container',
        labelQuoteAndPrice: '.js-total-label-quote-and-price',
        labelQuote: '.js-total-label-quote'
    }
};

/**
 * @constant {Object} attributes - All used attributes
 */
const attributes = {
    stepId: 'data-bscan-step-id',
    stepName: 'data-bscan-step-name',
    stepType: 'data-bscan-step-type',
    exclusive: 'data-bscan-exclude',
    productID: 'data-pid',
    productPrice: 'data-product-price',
    productPriceType: 'data-product-price-type'
};

const classes = {
    active: 'active',
    adviceAnimation: 'advice-animation'
};

/**
 * @constant {string} stepEvent - The event step
 */
const stepEvent = 'bscan:step';

/**
 * @let {JSON} advice - The returned advice
 */
let advice = {};

/**
 * @private updateAdviceObject
 * @param {string} pid - The product ID
 * @param {boolean} status - Add the product to the basket, yes or no.
 */
function updateAdviceObject(pid, status) {
    if (!advice) {
        return;
    }

    advice.forEach((category) => {
        category.products.forEach((product, index) => {
            if (product.id === pid) {
                category.products[index].recommended = status; // eslint-disable-line
            }
        });
    });

    setActiveProductIDs(advice); // eslint-disable-line no-use-before-define

    window.advice = advice;
}

/**
 * @private setupAdviceObject
 */
function setupAdviceObject() {
    const element = document.querySelector(selectors.adviceJSON);
    if (element && element.value) {
        advice = JSON.parse(element.value);
        document.removeEventListener(stepEvent, setupAdviceStep); // eslint-disable-line no-use-before-define
    }
}

/**
 * @private setupAdviceStep
 * @param {CustomEvent} evt - Custom event when stepping trough the application
 */
function setupAdviceStep(evt) {
    if (evt.detail === CONST.bscanSteps.ADVICE || document.querySelector(selectors.adviceStep)) {
        setupAdviceObject();
    }
}

/**
 * @private setupShareAdvice
 */
function setupShareAdvice() {
    document.addEventListener(
        'keydown',
        (event) => {
            if (event.ctrlKey && event.shiftKey && event.code === 'KeyS') {
                const ctcElement = document.querySelector(`${selectors.adviceStep} ${selectors.ctcElement}`);

                if (ctcElement) {
                    ctcElement.click();
                }
            }
        },
        false
    );
}

/**
 * @public initAdvice
 */
function initAdvice() {
    setupAdviceObject();
    setupShareAdvice();

    setTimeout(() => {
        document.querySelector(selectors.adviceStep).classList.remove(classes.adviceAnimation);
    }, 5500);
}

// Set initial value for the advice object once received.
document.addEventListener(stepEvent, setupAdviceStep);
window.addEventListener(CONST.events.load, setupAdviceObject);

const activeAdviceStep = document.querySelector(`${selectors.adviceStep}.${classes.active}`);
if (activeAdviceStep) {
    initAdvice();
}

// When dynamic modals are being loaded, update the advice object and
// set the product IDs inside the modal if available
document.addEventListener(
    'modalLoaded',
    (event) => {
        const currentStep = event.detail.modal;
        const modalId = currentStep.getAttribute('id');

        updateAdviceObject(currentStep);
        setActiveProductIDs(advice, currentStep);

        if (modalId !== 'lead-modal') {
            const currentStepName = currentStep.querySelector(`[${attributes.stepName}]`).getAttribute(attributes.stepName);
            sendGtmRouteChangeEvent(currentStepName);
        }

        if (modalId === 'modal-lets-talk') initLetsTalk(currentStep);
    },
    false
);

document.addEventListener(
    'modalStep',
    (event) => {
        const { currentStep, newStep } = event.detail;
        const currentStepName = currentStep.getAttribute(attributes.stepName);
        const newStepId = newStep.getAttribute(attributes.stepId);
        const newStepName = newStep.getAttribute(attributes.stepName);
        let updatedName = newStepName;

        // On the thank you page, append the previous step's name to make the title unique
        if (newStepId === 'bs-thank-you') {
            updatedName = `${newStepName}-${currentStepName.toLowerCase().replace(/\s/g, '-')}`;
        }

        sendGtmRouteChangeEvent(updatedName);
        updateAdviceObject(newStep);
    },
    false
);

export { initAdvice };
